import type { CSSProperties, JSX } from 'react';
import type { Palette } from '../../../_theme/palette.js';
import { hexWithAlpha } from '../../../_theme/palette.js';
import type { AnnouncementShape } from '../types.js';

interface CompactBannerBodyProps {
  palette: Palette;
  announcement: AnnouncementShape | null;
  onExpand: () => void;
  onDismiss: () => void;
  onCtaClick?: () => void;
  // "stack" = title above description (toast). "inline" = horizontal strip
  // with badge, em-dash separator, optional CTA pill (banner).
  variant?: 'inline' | 'stack';
}

export function CompactBannerBody({
  palette,
  announcement,
  onExpand,
  onDismiss,
  onCtaClick,
  variant = 'inline',
}: CompactBannerBodyProps): JSX.Element {
  const title = announcement?.title ?? 'Announcement';
  const description = announcement?.description;
  const badge = announcement?.badge;
  const badgeColor = announcement?.badgeColor ?? palette.primary;
  const ctaEnabled = Boolean(announcement?.cta?.enabled && announcement.cta.label);
  const ctaLabel = announcement?.cta?.label ?? '';
  const isStacked = variant === 'stack';
  // Dark backgrounds get a different button treatment (filled white) so the
  // CTA reads as a primary action against the dark strip. Light backgrounds
  // keep the theme-coloured filled pill.
  const isDarkBg = isLightOnDark(palette);

  const triggerCta = (event: React.MouseEvent): void => {
    event.stopPropagation();
    if (announcement?.cta?.url) {
      const target = announcement.cta.url;
      try {
        window.open(target, '_blank', 'noopener,noreferrer');
      } catch {
        window.location.href = target;
      }
    }
    onCtaClick?.();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onExpand}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onExpand();
        }
      }}
      style={rootStyle(isStacked)}
    >
      {!isStacked && !badge && <BannerIcon palette={palette} isDarkBg={isDarkBg} />}
      {badge && (
        <span style={badgeStyle(palette, badgeColor, isDarkBg)}>{badge}</span>
      )}
      <div style={textStyle(isStacked)}>
        <span style={titleStyle(palette, isStacked)}>{title}</span>
        {description &&
          (isStacked ? (
            <span style={descriptionStyle(palette, isStacked)}>{description}</span>
          ) : (
            <>
              <span style={separatorStyle(palette)} aria-hidden="true">—</span>
              <span style={descriptionStyle(palette, isStacked)}>{description}</span>
            </>
          ))}
      </div>
      {!isStacked && ctaEnabled && (
        <button
          type="button"
          onClick={triggerCta}
          style={ctaButtonStyle(palette, isDarkBg)}
          onMouseEnter={(event) => {
            event.currentTarget.style.transform = 'translateY(-1px)';
            event.currentTarget.style.filter = 'brightness(1.05)';
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.transform = 'translateY(0)';
            event.currentTarget.style.filter = 'none';
          }}
        >
          {ctaLabel}
        </button>
      )}
      <button
        type="button"
        aria-label="Close"
        onClick={(event) => {
          event.stopPropagation();
          onDismiss();
        }}
        style={closeButtonStyle(palette, isDarkBg)}
        onMouseEnter={(event) =>
          (event.currentTarget.style.background = hexWithAlpha(
            isDarkBg ? '#ffffff' : palette.text,
            0.1,
          ))
        }
        onMouseLeave={(event) => (event.currentTarget.style.background = 'transparent')}
      >
        ×
      </button>
    </div>
  );
}

// Megaphone glyph that defaults to the palette primary tint. Subtle on
// light themes (filled chip), inverted on dark themes (white chip).
function BannerIcon({ palette, isDarkBg }: { palette: Palette; isDarkBg: boolean }): JSX.Element {
  const tint = isDarkBg ? hexWithAlpha('#ffffff', 0.16) : hexWithAlpha(palette.primary, 0.12);
  const color = isDarkBg ? '#ffffff' : palette.primary;
  return (
    <span
      aria-hidden="true"
      style={{
        width: 32,
        height: 32,
        borderRadius: 9,
        background: tint,
        color,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
        <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
      </svg>
    </span>
  );
}

// Treat the palette background as "dark" if it's noticeably darker than
// mid-grey. Lets the banner flip its CTA and close-button treatments
// without needing a separate `dark: boolean` plumbed in from the theme.
function isLightOnDark(palette: Palette): boolean {
  const hex = (palette.background || '').replace('#', '');
  if (hex.length !== 6 && hex.length !== 3) return false;
  const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  // Perceived luminance — same formula the WCAG contrast check uses.
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
}

function rootStyle(isStacked: boolean): CSSProperties {
  // No `justify-content: center` — items hug the left and the CTA/close
  // cluster pushes itself to the right via the text region's flex: 1.
  // Inline banners run flush to the 86 % rail (horizontal padding is
  // owned by BannerShell so the content actually fills the rail).
  // Stacked toasts keep their own padding because they live inside a
  // different shell that doesn't provide one.
  return {
    display: 'flex',
    alignItems: isStacked ? 'flex-start' : 'center',
    gap: 12,
    padding: isStacked ? '14px 14px 14px 16px' : '14px 0',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    minHeight: isStacked ? undefined : 56,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  };
}

function badgeStyle(palette: Palette, color: string, isDarkBg: boolean): CSSProperties {
  const accent = isDarkBg ? '#ffffff' : color;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: 999,
    background: isDarkBg ? hexWithAlpha('#ffffff', 0.16) : hexWithAlpha(color, 0.14),
    color: accent,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    flexShrink: 0,
    lineHeight: 1.4,
    fontFamily: palette.font,
  };
}

function textStyle(isStacked: boolean): CSSProperties {
  if (isStacked) {
    return {
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      minWidth: 0,
      flex: 1,
    };
  }
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    minWidth: 0,
    overflow: 'hidden',
    // flex: 1 expands the text region to fill the row so the CTA + close
    // get pushed to the right edge. Without this the row collapses to its
    // content width and the whole banner reads as a centered chip.
    flex: 1,
  };
}

function titleStyle(palette: Palette, isStacked: boolean): CSSProperties {
  return {
    fontSize: 14,
    fontWeight: 600,
    color: palette.text,
    fontFamily: palette.font,
    whiteSpace: isStacked ? 'normal' : 'nowrap',
    overflow: isStacked ? 'visible' : 'hidden',
    textOverflow: isStacked ? 'unset' : 'ellipsis',
    lineHeight: 1.4,
    flexShrink: 0,
  };
}

function separatorStyle(palette: Palette): CSSProperties {
  return {
    color: palette.textMuted,
    flexShrink: 0,
    lineHeight: 1.4,
    fontSize: 13,
  };
}

function descriptionStyle(palette: Palette, isStacked: boolean): CSSProperties {
  if (isStacked) {
    return {
      fontSize: 13,
      color: palette.textMuted,
      fontFamily: palette.font,
      lineHeight: 1.45,
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    };
  }
  return {
    fontSize: 13,
    color: palette.textMuted,
    fontFamily: palette.font,
    lineHeight: 1.4,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  };
}

function ctaButtonStyle(palette: Palette, isDarkBg: boolean): CSSProperties {
  // Dark banner → white pill, ink text. Light banner → primary-coloured
  // pill, white text. Consistent shape, theme-aware contrast.
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '7px 14px',
    borderRadius: 999,
    border: 'none',
    background: isDarkBg ? '#ffffff' : palette.primary,
    color: isDarkBg ? '#0f172a' : '#ffffff',
    fontFamily: palette.font,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'transform 160ms cubic-bezier(0.16,1,0.3,1), filter 160ms ease',
    boxShadow: isDarkBg ? '0 2px 8px rgba(15,23,42,0.18)' : '0 1px 0 rgba(15,23,42,0.04)',
  };
}

function closeButtonStyle(palette: Palette, isDarkBg: boolean): CSSProperties {
  return {
    width: 28,
    height: 28,
    borderRadius: 8,
    border: 'none',
    background: 'transparent',
    color: isDarkBg ? hexWithAlpha('#ffffff', 0.85) : palette.textMuted,
    fontSize: 18,
    lineHeight: 1,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'background 120ms ease',
  };
}
